/**
 * Flushes staged denial/query reasons from UserStore to the bucket upsert API.
 *
 * Builds one upsert payload per staged entry and submits them sequentially.
 * Does not refetch bucket totals after success.
 *
 * @module flushDenialQueryReasonsToApi
 */

import { createLogger } from "@risalabs_frontend_org/logger";
import {
  DenialQueryBucketService,
  DenialQueryBucketUpsertPayload,
} from "../../../../api/services/denialQueryBucketService";
import { getLocalDenialQueryBucketTimeContext } from "../../../../utils/formatDate";
import {
  clearDenialQueryReasonsFromStoreByOrderId,
  DenialQueryReasonStoreEntry,
  getDenialQueryReasonsFromStore,
} from "./denialQueryReasonUserStoreHelper";

const log = createLogger(
  "DenialQueryReasonStore",
  "flushDenialQueryReasonsToApi.ts",
);

/** Inputs required to flush staged denial/query reasons for an order. */
export interface FlushDenialQueryReasonsParams {
  /** Medical PA order ID whose staged reasons should be persisted. */
  orderId: string;
  /** Regimen associated with the order. */
  regimenName: string;
}

/**
 * Maps a staged UserStore entry into a denial/query bucket upsert payload.
 *
 * @param entry - Staged reason from UserStore.
 * @param regimenName - Regimen associated with the order.
 * @param date - Local calendar date for the bucket update.
 * @param timeOffset - Local timezone offset in minutes.
 * @returns Payload accepted by the denial/query bucket upsert endpoint.
 */
const buildUpsertPayload = (
  entry: DenialQueryReasonStoreEntry,
  regimenName: string,
  date: string,
  timeOffset: number,
): DenialQueryBucketUpsertPayload => ({
  order_id: entry.order_id,
  regimen_name: regimenName,
  type: entry.type,
  date,
  time_offset: timeOffset,
  reason_bucket: [{ category: undefined, reason_bucket: entry.reason }],
});

/**
 * Creates upsert payloads from UserStore and submits each one sequentially.
 *
 * On full success, clears staged reasons for the given order. Skips work when
 * no matching entries exist. Does not call the denial/query bucket fetcher.
 *
 * @param params - Order ID and regimen name used to build API payloads.
 * @returns A promise that resolves after all upserts succeed, or immediately
 *   when there is nothing to flush.
 * @throws {Error} When any upsert request fails.
 */
export const flushDenialQueryReasonsToApi = async ({
  orderId,
  regimenName,
}: FlushDenialQueryReasonsParams): Promise<void> => {
  const entries = getDenialQueryReasonsFromStore().filter(
    (entry) => entry.order_id === orderId,
  );

  if (entries.length === 0) {
    log.debug("No staged denial/query reasons to flush", { orderId });
    return;
  }

  const { date, time_offset } = getLocalDenialQueryBucketTimeContext();
  const payloads = entries.map((entry) =>
    buildUpsertPayload(entry, regimenName, date, time_offset),
  );

  log.info("Flushing denial/query reasons", {
    orderId,
    count: payloads.length,
    payloads,
  });

  for (const payload of payloads) {
    log.info("Upserting denial/query reason bucket", payload);
    await DenialQueryBucketService.upsertDenialQueryBucket(payload);
  }

  clearDenialQueryReasonsFromStoreByOrderId(orderId);
  log.info("Flushed and cleared denial/query reasons", { orderId });
};
